import { useState } from 'react'
import type { JSX } from 'react'

import { Skeleton } from '@buffer-mono/popcorn'

import styles from './MetricsSection.module.css'

interface ChartTypePreviewProps {
  src: string
}

// Radix unmounts HoverCard.Content on close, so this remounts on every reopen.
// Remember which previews have loaded so a cached one doesn't re-flash the
// shimmer the second time its popover opens.
const loadedPreviews = new Set<string>()

/**
 * Preview graphic for a chart-type hover popover. Reserves the image's aspect
 * ratio (no layout shift when the popover opens) and overlays a loading shimmer
 * that unmounts on load, so it can't show through the graphic's rounded corners.
 */
export function ChartTypePreview({ src }: ChartTypePreviewProps): JSX.Element {
  const [loaded, setLoaded] = useState(() => loadedPreviews.has(src))

  const handleLoaded = (): void => {
    loadedPreviews.add(src)
    setLoaded(true)
  }

  return (
    <div className={styles.chartTypePreview}>
      <img
        className={styles.chartTypePreviewImage}
        src={src}
        alt=""
        data-testid="chart-type-preview-image"
        onLoad={handleLoaded}
        // Drop the shimmer if the graphic fails to load (but don't cache it as
        // loaded — a later retry should still get the shimmer).
        onError={() => setLoaded(true)}
      />
      {!loaded && (
        <span
          className={styles.chartTypePreviewShimmer}
          data-testid="chart-type-preview-shimmer"
        >
          <Skeleton
            className={styles.chartTypePreviewSkeleton}
            width="100%"
            height="100%"
          />
        </span>
      )}
    </div>
  )
}
